import { Label, FormHelp } from '@zillow/constellation';
import PropTypes from 'prop-types';
import { useCallback, useEffect } from 'react';
import LabeledAddressInputAutocompleteField from '../../common/LabeledAddressInputAutocompleteField';
import { queryAutocomplete, getRegionInformation } from '../api';
import { INPUTS } from '../constants';

const { MODES } = LabeledAddressInputAutocompleteField;

const VALIDATION_KEYS = [INPUTS.cityOrZip, INPUTS.zipCode, INPUTS.city];

const DEFAULT_REGISTER_PROPS = {
    required: 'City or zip code is required',
};

const mode = [MODES.CITY, MODES.ZIP];

const LocationField = ({ register, setValue, errors, trigger, clearErrors, ...rest }) => {
    useEffect(() => {
        register(INPUTS.cityOrZip, DEFAULT_REGISTER_PROPS);
        register(INPUTS.city, DEFAULT_REGISTER_PROPS);
        register(INPUTS.zipCode, DEFAULT_REGISTER_PROPS);
        register(INPUTS.state);
    }, [register]);

    const triggerValidation = useCallback(() => trigger(VALIDATION_KEYS), [trigger]);

    const handleOnChange = useCallback(
        value => {
            clearErrors(VALIDATION_KEYS);
            setValue(INPUTS.cityOrZip, value);

            // reset other values
            setValue(INPUTS.zipCode, '');
            setValue(INPUTS.city, '');
            setValue(INPUTS.state, '');
        },
        [setValue, clearErrors]
    );

    const handleOnSelection = useCallback(
        async ({ display, city, zipCode, state }) => {
            await setValue(INPUTS.cityOrZip, display);
            await setValue(INPUTS.zipCode, zipCode);
            await setValue(INPUTS.city, city);
            await setValue(INPUTS.state, state);
            triggerValidation();
        },
        [setValue, triggerValidation]
    );

    const error = errors.cityOrZip || errors.zipCode || errors.city;

    return (
        <LabeledAddressInputAutocompleteField
            mode={mode}
            aria-label="Location"
            renderLabel={labelProps => <Label {...labelProps}>Location</Label>}
            description={<FormHelp>Enter the city or zip code for the area you’re looking to buy in.</FormHelp>}
            error={error}
            queryAutocomplete={queryAutocomplete}
            getRegionInformation={getRegionInformation}
            onChange={handleOnChange}
            onBlur={triggerValidation}
            onSelection={handleOnSelection}
            {...rest}
        />
    );
};

LocationField.propTypes = {
    register: PropTypes.func.isRequired,
    setValue: PropTypes.func.isRequired,
    trigger: PropTypes.func.isRequired,
    clearErrors: PropTypes.func.isRequired,
    errors: PropTypes.object,
};

LocationField.defaultProps = {
    errors: {},
};

export default LocationField;
